import { HorizontalBar, mixins } from "vue-chartjs";
import "chartjs-plugin-annotation";
import ChartJsPluginDataLabels from "chartjs-plugin-datalabels";
import Router from "../router";

const { reactiveProp } = mixins;

export default {
  extends: HorizontalBar,

  mixins: [reactiveProp], // chartData prop --> https://vue-chartjs.org/guide/#updating-charts

  props: {
    profileNames: {
      type: Array,
      default: () => []
    },

    isPvsPChallenge: {
      type: Boolean,
      default: false
    },

    isTeamChallenge: {
      type: Boolean,
      default: false
    },

    showLegend: {
      type: Boolean,
      default: false
    },

    legendPosition: {
      type: String,
      default: "top",
      validator: function(value) {
        // The value must match one of these strings
        return ["bottom", "top", "left", "right"].includes(value);
      }
    },

    showYLabels: {
      type: Boolean,
      default: false
    },

    showPacer: {
      type: Boolean,
      default: false
    },

    isWellnessChallengeOverview: {
      type: Boolean,
      default: false
    },

    maxX: {
      type: Number,
      default: undefined
    },

    goalLine: {
      type: Number,
      default: undefined
    },

    isOrganizationLeaderboard: {
      type: Number,
      default: 0
    }
  },

  data() {
    const _this = this;
    const isTeamChallenge = this.isTeamChallenge;
    const showYLabels = this.showYLabels;
    const showPacer = this.showPacer;
    const maxX = this.maxX;
    let alignValues = [];
    let profileNames = this.profileNames;

    return {
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: {
          display: this.showLegend,
          position: this.legendPosition,
          labels: {
            fontFamily: "Manrope, Lato, sans-serif",
            fontSize: 24
          },
          onClick: function(e, legendItem) {
            // default onclick handler
            var index = legendItem.datasetIndex;
            var ci = this.chart;
            var meta = ci.getDatasetMeta(index);

            // See controller.isDatasetVisible comment
            meta.hidden =
              meta.hidden === null ? !ci.data.datasets[index].hidden : null;

            // We hid a dataset ... rerender the chart
            ci.update();
            // end default onclick handler

            // emit dataset index in order to sort according to filter
            _this.$emit("legendClicked", { index, isHidden: meta.hidden });
          }
        },
        plugins: {
          datalabels: {
            display: function(context) {
              let { datasetIndex } = context;
              let ci = context.chart;
              let datasets = context.chart.data.datasets;
              let firstDatasetNotHiddenIndex = datasets.findIndex(
                (_, index) => {
                  let meta = ci.getDatasetMeta(index);
                  return !meta.hidden;
                }
              );
              // For stacked bar charts the label will display on each dataset.
              // Since in our case the label is the username and only needs to be displayed once,
              //  here we only show the label on the first dataset.
              return datasetIndex === firstDatasetNotHiddenIndex;
            },
            anchor: function(context) {
              let { dataIndex } = context;
              return showPacer && dataIndex === 0 ? "start" : "end";
            },
            color: function(context) {
              let { dataIndex } = context;
              let darkColor = showPacer && dataIndex === 0 ? "grey" : "black";

              return context.hovered ||
                alignValues[dataIndex] === "end" ||
                (showPacer && dataIndex === 0)
                ? darkColor
                : "white";
            },
            align: function(context) {
              let { chart, dataIndex, dataset } = context;
              let value = dataset.data[dataIndex];
              let secondTick = chart.scales["x-axis-0"].ticksAsNumbers[2];
              let align =
                value < secondTick || (showPacer && dataIndex === 0)
                  ? "end"
                  : "start";
              alignValues[dataIndex] = align;
              return align;
            },
            backgroundColor: function(context) {
              let { dataIndex } = context;

              return context.hovered || (showPacer && dataIndex === 0)
                ? "white"
                : "";
            },
            borderRadius: 4,
            font: {
              size: 18,
              weight: 600
            },
            padding: function(context) {
              let { dataIndex } = context;

              return showPacer && dataIndex === 0 ? 5 : 1;
            },
            margin: 5,
            formatter: function(value, context) {
              let { chart, dataIndex } = context;
              return chart.data.labels[dataIndex];
            },
            listeners: {
              enter: function(context) {
                // Receives `enter` events for any labels of any dataset. Indices of the
                // clicked label are: `context.datasetIndex` and `context.dataIndex`.
                // For example, we can modify keep track of the hovered state and
                // return `true` to update the label and re-render the chart.
                context.hovered = true;
                return true;
              },
              leave: function(context) {
                // Receives `leave` events for any labels of any dataset.
                context.hovered = false;
                return true;
              },
              click: function(context) {
                // Don't navigate on click for PvsP challenges
                if (this.isPvsPChallenge) return;

                let { chart, dataIndex } = context;
                let entityName = chart.data.labels[dataIndex];
                if (isTeamChallenge) {
                  if (entityName !== "Pacer") {
                    Router.push({
                      name: "Team",
                      params: {
                        teamId: entityName.replace(/[^A-Za-z0-9]/g, "")
                      } // teamNameURL
                    });
                  }
                } else {
                  if (entityName !== "Pacer") {
                    Router.push({
                      name: "ProfilePage",
                      params: { username: entityName }
                    });
                  }
                }
                // if (isTeamChallenge) {
                //   Router.push({
                //     name: "Team",
                //     params: { teamId: entityName.replace(/[^A-Za-z0-9]/g, "") } // teamNameURL
                //   });
                // } else {
                //   Router.push({
                //     name: "ProfilePage",
                //     params: { username: entityName }
                //   });
                // }
              }
            }
          }
        },
        scales: {
          yAxes: [
            {
              stacked: true,
              gridLines: {
                display: false
              },
              ticks: {
                fontSize: 16,
                fontFamily: "Manrope, Lato, sans-serif",
                callback: function(label) {
                  if (showYLabels) {
                    // Include an @ sign in if label exists and it's not 'Pacer'
                    // note, a new line is created for each element of the array
                    let labelArray = [
                      label ? (label === "Pacer" ? label : "@" + label) : ""
                    ];

                    if (/\s/.test(labelArray[0])) {
                      labelArray = labelArray[0].split(" ");
                    }

                    return labelArray.map(labelSection => {
                      if (labelSection.length > 10) {
                        labelSection = labelSection.slice(0, 10);
                        return labelSection + "...";
                      }
                      return labelSection;
                    });
                  }
                }
              }
            }
          ],
          xAxes: [
            {
              stacked: true,
              ticks: {
                max: maxX,
                fontFamily: "Manrope, Lato, sans-serif",
                fontSize: 16,
                beginAtZero: true,
                callback: function(value) {
                  return Math.abs(value) > 999
                    ? Math.sign(value) * (Math.abs(value) / 1000).toFixed(1) +
                        "k"
                    : Math.sign(value) * Math.abs(value);
                }
              }
            }
          ]
        },
        onHover(event, chartElement) {
          // Add a pointer cursor on hover
          const left = this.chart.scales["y-axis-0"].left;
          const right = this.chart.scales["y-axis-0"].width;
          const top = this.chart.scales["y-axis-0"].top;
          const bottom = this.chart.scales["y-axis-0"].bottom;

          if (chartElement[0]) {
            // Don't show the pointer when hovering over Pacer's Chart Line
            if (chartElement[0]._model.label === "Pacer") {
              event.target.style.cursor = "default";
              return;
            }
          }

          if (
            (event.offsetY > top &&
              event.offsetY < bottom &&
              event.offsetX > left &&
              event.offsetX < right) ||
            chartElement[0]
          ) {
            event.target.style.cursor = "pointer";
          } else {
            event.target.style.cursor = "default";
          }
        },
        onClick(evt, activeElement) {
          let entityName = null;
          // If bar click, go to profile of user
          const activePoint = activeElement[0];

          if (activePoint !== undefined) {
            entityName = activePoint._view.label;
            if (isTeamChallenge) {
              if (entityName !== "Pacer") {
                Router.push({
                  name: "Team",
                  params: { teamId: entityName.replace(/[^A-Za-z0-9]/g, "") } // teamNameURL
                });
              }
            } else {
              if (entityName !== "Pacer") {
                Router.push({
                  name: "ProfilePage",
                  params: { username: profileNames[activePoint._index] }
                });
              }
            }
          }

          // If 'y-label' clicked, go to profile page of the username of that label
          // eslint-disable-next-line
          const mousePoint = Chart.helpers.getRelativePosition(
            evt,
            this.chart.chart
          );
          this.y_clicked = this.chart.scales["y-axis-0"].getValueForPixel(
            mousePoint.y
          );
          this.x_clicked = this.chart.scales["x-axis-0"].getValueForPixel(
            mousePoint.x
          );

          entityName = this.tooltip._data.labels[this.y_clicked];

          if (entityName && this.x_clicked < 0) {
            if (isTeamChallenge) {
              Router.push({
                name: "Team",
                params: { teamId: entityName.replace(/[^A-Za-z0-9]/g, "") } // teamNameURL
              });
            } else {
              if (entityName !== "Pacer") {
                Router.push({
                  name: "ProfilePage",
                  params: { username: entityName }
                });
              }
            }
          }
        },
        tooltips: {
          mode: "index",
          intersect: false, // false shows the tooltip when data is 0
          titleFontFamily: "Manrope, Lato, sans-serif",
          titleFontSize: 24,
          bodyFontFamily: "Manrope, Lato, sans-serif",
          bodyFontSize: 18,
          footerFontFamily: "Manrope, Lato, sans-serif",
          footerFontSize: 18,
          callbacks: {
            label(tooltipItem, data) {
              //  round and add thousandths separator to number
              let label = data.datasets[tooltipItem.datasetIndex].label || "";
              if (label) {
                label += ": ";
              }
              label += Math.round(tooltipItem.xLabel)
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
              return label;
            },
            footer: (tooltipItems, data) => {
              let footer = null;
              if (data && data.footers) {
                footer = data.footers[tooltipItems[0].index] || null;
              }
              if (footer) {
                return footer;
              }
              if (tooltipItems.length > 1) {
                let total = tooltipItems.reduce(
                  (acc, curr) => acc + parseInt(curr.xLabel),
                  0
                );
                return "Total: " + total.toLocaleString();
              }
            }
          }
        }
      }
    };
  },

  mounted() {
    if (this.goalLine) {
      this.options.annotation = {
        events: ["mouseenter"],
        annotations: [
          {
            type: "line",
            mode: "vertical",
            scaleID: "x-axis-0",
            value: this.goalLine,
            borderColor: this._app_chart_colors_multiple.navy,
            borderWidth: 4,
            label: {
              backgroundColor: this._app_chart_colors_multiple.teal,
              // Whether the label is enabled and should be displayed
              enabled: true,
              // Text to display in label - default is null. Provide an array to display values on a new line
              content: "Goal",
              position: "top",
              xAdjust:
                this.goalLine !== undefined &&
                this.chartData.datasets[0].data[0] > this.goalLine
                  ? -20
                  : 20
            },
            onMouseenter: function() {
              // ======================
              // if setting label.enabled = false
              // then the below is a way to show the label when hover over the line
              // this is also used and active in BarChart
              // ======================
              // let element = this;
              // element.chartInstance.options.annotation.annotations[0].label.enabled = true;
              // element.chartInstance.chart.update();
              // setTimeout(() => {
              //   element.chartInstance.options.annotation.annotations[0].label.enabled = false;
              //   element.chartInstance.chart.update();
              // }, 900);
            }
          }
        ],
        drawTime: "afterDatasetsDraw"
      };
    }
    if (!this.showYLabels) {
      this.addPlugin(ChartJsPluginDataLabels);
    }
    if (this.showPacer) {
      this.addPlugin({
        id: "pacer-image",
        // beforeDraw vs afterDraw - chart tooltips/etc need to be "drawn" after this image in order to appear on top.
        // so we use beforeDraw for this image to be below other chart elements
        beforeDraw: chart => {
          let datasetMeta = chart.getDatasetMeta(0);
          let endOfBarX = datasetMeta.data[0]._view.x;
          let endOfBarY = datasetMeta.data[0]._view.y;
          let barHeight = datasetMeta.data[0]._view.height;
          let image = new Image();
          image.src = "https://cdn.tbybewell.com/images/icon_pacer_rabbit.svg";
          chart.chart.ctx.drawImage(
            image,
            endOfBarX + 15,
            endOfBarY - barHeight / 2,
            barHeight,
            barHeight
          );
        }
      });
    }

    this.$emit("add-background-color", this.addBackgroundColor());

    this.renderChart(this.chartData, this.options);
    this.renderChart(this.chartData, this.options); // hack hack hack - rendering twice let's us dynamically change the label color based on align
  },

  methods: {
    addBackgroundColor() {
      let canvas = this.$refs.canvas.getContext("2d");
      const colors = this._app_chart_colors_multiple;

      return context => {
        const colorKeys = Object.keys(colors);
        let colorKey = colorKeys[context.dataIndex % colorKeys.length];
        let color = colors[colorKey];
        if (context.dataIndex === 0 && this.showPacer) {
          // Chart Line color for Pacer
          color = "#1F9E9E";
        }
        let gradientFill = canvas.createLinearGradient(
          0,
          0,
          context.chart.width,
          0
        );
        gradientFill.addColorStop(0, "white");
        gradientFill.addColorStop(0.5, color);
        return gradientFill;
      };
    }
  }
};
